// frontend/src/components/dedup/FinalListPreview.tsx
import { getDedupUrl, authFetch } from '../../config';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';

interface FinalRecord {
  id: number;
  source_type: 'external' | 'internal';
  source_priority: number;
  anrede: string;
  vorname: string;
  nachname: string;
  strasse: string;
  plz: string;
  ort: string;
  telefon?: string;
  email?: string;
  additional_data?: any;
}

interface Props {
  campaignId: number;
}

export default function FinalListPreview({ campaignId }: Props) {
  const [records, setRecords] = useState<FinalRecord[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<FinalRecord[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    from_external: 0,
    from_internal: 0
  });
  const [selectedRecord, setSelectedRecord] = useState<FinalRecord | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadFinalList();
  }, [campaignId]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = records.filter(record => {
        const search = searchTerm.toLowerCase();
        return (
          record.vorname?.toLowerCase().includes(search) ||
          record.nachname?.toLowerCase().includes(search) ||
          record.plz?.includes(search) ||
          record.ort?.toLowerCase().includes(search) ||
          record.email?.toLowerCase().includes(search) ||
          record.telefon?.includes(search)
        );
      });
      setFilteredRecords(filtered);
    } else {
      setFilteredRecords(records);
    }
  }, [searchTerm, records]);

  const loadFinalList = async () => {
    setLoading(true);
    try {
      const response = await authFetch(
        getDedupUrl(`/campaigns/${campaignId}/final-list`)
      );
      const result = await response.json();
      
      if (result.success) {
        setRecords(result.records);
        setFilteredRecords(result.records);
        setStats(result.stats);
      }
    } catch (error) {
      console.error('Error loading final list:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: 'csv' | 'xlsx') => {
    try {
      const response = await authFetch(
        getDedupUrl(`/campaigns/${campaignId}/export?format=${format}`)
      );
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dedup_campaign_${campaignId}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting:', error);
      alert('Fehler beim Exportieren');
    }
  };

  const handleViewDetails = (record: FinalRecord) => {
    setSelectedRecord(record);
    setDetailsOpen(true);
  };

  const handleRemoveRecord = async (recordId: number) => {
    if (!window.confirm('Datensatz wirklich aus der finalen Liste entfernen?')) return;

    try {
      await authFetch(
        getDedupUrl(`/final-list/${recordId}`),
        { method: 'DELETE' }
      );
      await loadFinalList();
    } catch (error) {
      console.error('Error removing record:', error);
      alert('Fehler beim Entfernen');
    }
  };

  const getSourceTypeColor = (type: string) => {
    return type === 'external' ? 'primary' : 'secondary';
  };

  return (
    <Box>
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
        gap: 3,
        mb: 3 
      }}>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="h4" color="primary">
            {stats.total.toLocaleString('de-DE')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gesamt Datensätze
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="h4" color="primary.main">
            {stats.from_external.toLocaleString('de-DE')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Aus externen Quellen
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="h4" color="secondary.main">
            {stats.from_internal.toLocaleString('de-DE')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Aus internen Daten
          </Typography>
        </Paper>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6">
              Deduplizierte Liste
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => handleExport('csv')}
              >
                CSV Export
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => handleExport('xlsx')}
              >
                Excel Export
              </Button>
            </Box>
          </Box>

          <TextField
            fullWidth
            placeholder="Suche nach Name, PLZ, Ort, E-Mail, Telefon..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          {filteredRecords.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                {searchTerm ? 'Keine Ergebnisse gefunden' : 'Keine Datensätze vorhanden'}
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Prio</TableCell>
                      <TableCell>Typ</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Adresse</TableCell>
                      <TableCell>Kontakt</TableCell>
                      <TableCell align="right">Aktionen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRecords.slice(0, 100).map((record) => (
                      <TableRow key={record.id} hover>
                        <TableCell>
                          <Chip 
                            label={`#${record.source_priority}`}
                            size="small"
                            color={record.source_priority === 1 ? 'error' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={record.source_type === 'external' ? 'Extern' : 'Intern'}
                            size="small"
                            color={getSourceTypeColor(record.source_type)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {record.anrede} {record.vorname} {record.nachname}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {record.strasse}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {record.plz} {record.ort}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {record.email && (
                            <Typography variant="caption" display="block">
                              📧 {record.email}
                            </Typography>
                          )}
                          {record.telefon && (
                            <Typography variant="caption" display="block">
                              📞 {record.telefon}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Details anzeigen">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(record)}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Entfernen">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveRecord(record.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {filteredRecords.length > 100 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                  Zeige 100 von {filteredRecords.length.toLocaleString('de-DE')} Datensätzen
                </Typography>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Datensatz-Details
        </DialogTitle>
        <DialogContent>
          {selectedRecord && (
            <Box sx={{ pt: 2, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Anrede</Typography>
                <Typography variant="body1">{selectedRecord.anrede || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Quellen-Typ</Typography>
                <Typography variant="body1">
                  <Chip 
                    label={selectedRecord.source_type === 'external' ? 'Extern' : 'Intern'}
                    size="small"
                    color={getSourceTypeColor(selectedRecord.source_type)}
                  />
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Vorname</Typography>
                <Typography variant="body1">{selectedRecord.vorname || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Nachname</Typography>
                <Typography variant="body1">{selectedRecord.nachname || '-'}</Typography>
              </Box>
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Typography variant="caption" color="text.secondary">Straße</Typography>
                <Typography variant="body1">{selectedRecord.strasse || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">PLZ</Typography>
                <Typography variant="body1">{selectedRecord.plz || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Ort</Typography>
                <Typography variant="body1">{selectedRecord.ort || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">E-Mail</Typography>
                <Typography variant="body1">{selectedRecord.email || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Telefon</Typography>
                <Typography variant="body1">{selectedRecord.telefon || '-'}</Typography>
              </Box>
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Typography variant="caption" color="text.secondary">Priorität</Typography>
                <Typography variant="body1">Quelle #{selectedRecord.source_priority}</Typography>
              </Box>
              {selectedRecord.additional_data && (
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Typography variant="caption" color="text.secondary">Zusatzdaten</Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                    <pre style={{ margin: 0, fontSize: '12px', overflow: 'auto' }}>
                      {JSON.stringify(selectedRecord.additional_data, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}